/**
 * The context an assigned task hands to a practice tool: which learner, which
 * instrument, which task, and where to go back to. A link only ever carries
 * what the task itself established; the tool checks it again before using it,
 * and the server still validates the instrument on save.
 */
export interface PracticeToolContext {
  /** The learner the task belongs to. Kept in the address so the tool can compare it with who is selected. */
  learner: string | null;
  todo: string | null;
  /** The instrument the task says it is for, or null when the task does not say. */
  instrument: string | null;
  /** An in-app `/practice` path to return to, already validated. */
  returnTo: string | null;
}

const ORIGIN = "http://practice-return.invalid";

/**
 * Accepts only a path inside `/practice` on this site (the practice page, its
 * tools, and a `#practice-task-…` anchor). Anything that could leave the site
 * or reach another area — protocol-relative, backslash, scheme, encoded
 * variants, control characters — is refused rather than repaired.
 */
export function safePracticeReturnPath(value: string | null | undefined): string | null {
  if (typeof value !== "string" || value === "" || value.length > 300) return null;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }
  if (decoded.startsWith("//") || decoded.includes("\\")) return null;
  let url: URL;
  try {
    url = new URL(value, ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== ORIGIN) return null;
  if (url.pathname !== "/practice" && !url.pathname.startsWith("/practice/")) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The page anchor of one task row. */
export const practiceTaskAnchor = (todoId: string): string => `practice-task-${todoId.replace(/[^A-Za-z0-9_:-]/g, "")}`;

export type PracticeToolName = "metronome" | "groove";

/**
 * A link from a task into a tool. `student` asks the family shell to select the
 * learner (it only honours a learner the account is linked to); `learner` stays
 * in the address for the tool to compare against who is actually selected.
 */
export function practiceToolHref(
  base: string,
  context: {
    studentId: string;
    todoId: string;
    instrument: string | null;
    returnTo: string;
    /**
     * `todo` asks the metronome to load this task's preset, so use it only for a task that has one.
     * `task` just names the task for the way back and the notes, and loads nothing.
     */
    as?: "todo" | "task";
  },
): string {
  const [path, existing = ""] = base.split("?");
  const params = new URLSearchParams(existing);
  params.set("student", context.studentId);
  params.set("learner", context.studentId);
  params.set(context.as ?? "task", context.todoId);
  if (context.instrument) params.set("instrument", context.instrument);
  const returnTo = safePracticeReturnPath(context.returnTo);
  if (returnTo) params.set("returnTo", returnTo);
  return `${path}?${params.toString()}`;
}

export function readPracticeToolContext(search: string): PracticeToolContext {
  const params = new URLSearchParams(search);
  const text = (key: string, max: number) => {
    const value = params.get(key)?.trim();
    return value && value.length <= max ? value : null;
  };
  return {
    learner: text("learner", 100),
    todo: text("todo", 120) ?? text("task", 120),
    instrument: text("instrument", 80),
    returnTo: safePracticeReturnPath(params.get("returnTo")),
  };
}

/**
 * The instrument to preselect: the task's, but only when the link was made for
 * the learner who is selected now. A link for someone else preselects nothing.
 */
export function preferredToolInstrument(context: PracticeToolContext, selectedLearner: string | null): string | undefined {
  if (!context.instrument || !selectedLearner || context.learner !== selectedLearner) return undefined;
  return context.instrument;
}

export type ToolContextNote = "learner-differs" | "instrument-unconfirmed" | "instrument-differs" | null;

/**
 * What to tell the person when a task link could not be fully honoured.
 * `switchPending` is true while the shell has yet to apply a `student=` switch,
 * so a link is not reported as wrong for a moment that is about to pass.
 */
export function toolContextNote(
  context: PracticeToolContext,
  state: { selectedLearner: string | null; instruments: readonly string[]; loading: boolean; switchPending: boolean; selectedInstrument?: string },
): ToolContextNote {
  if (!context.todo || state.switchPending) return null;
  if (context.learner && state.selectedLearner && context.learner !== state.selectedLearner) return "learner-differs";
  if (state.loading) return null;
  if (!context.instrument || !state.instruments.includes(context.instrument)) {
    // A learner with exactly one lesson instrument is still confirmed by their lessons, not by the task.
    return "instrument-unconfirmed";
  }
  // The task names a verified instrument but something else is selected (a choice, or a run in progress): say so.
  if (state.selectedInstrument !== undefined && state.selectedInstrument !== context.instrument) return "instrument-differs";
  return null;
}
